import type { CustomRole, Task } from '@nexo/types';
import { cn } from '@nexo/ui';
import { useContext, type ReactNode } from 'react';
import { TeamContext } from '../../layouts/TeamContext';
import { useAuth } from '../auth/AuthProvider';
import { isDesktop, openExternal } from '../../lib/platform';
import { roleColors } from '../teams/roles';
import { openUserCard } from '../teams/UserCard';
import { TypeIcon } from '../tasks/TaskIcons';
import { keys } from '../../lib/queries';
import { useQueryClient } from '@tanstack/react-query';
import { parseMarkdown, type MdNode } from './markdown';
import { t } from '../../lib/i18n';

interface MentionContext {
  nameOf: (userId: string) => string | undefined;
  roleOf: (roleId: string) => CustomRole | undefined;
  meId: string | undefined;
  myRoleIds: string[];
  /** The team's task with this key, if it is ours and known. */
  taskOf: (key: string, number: number) => Task | undefined;
  openTask: (taskId: string) => void;
}

function render(nodes: MdNode[], ctx: MentionContext, key = ''): ReactNode[] {
  return nodes.map((node, i) => {
    const k = `${key}${i}`;
    switch (node.type) {
      case 'text':
        return <span key={k}>{node.text}</span>;
      case 'link':
        return (
          <a
            key={k}
            href={node.href}
            target="_blank"
            rel="noreferrer noopener"
            onClick={(event) => {
              if (!isDesktop) return;
              event.preventDefault();
              void openExternal(node.href);
            }}
            className="text-accent underline decoration-accent/30 underline-offset-2 hover:decoration-accent"
          >
            {node.text}
          </a>
        );
      case 'code':
        return (
          <code
            key={k}
            className="rounded-md bg-surface-3/70 px-1.5 py-0.5 font-mono text-[0.86em]"
          >
            {node.text}
          </code>
        );
      case 'codeblock':
        return (
          <pre
            key={k}
            className="scrollbar-thin my-1.5 overflow-x-auto rounded-lg border border-border bg-surface-2 p-3 font-mono text-[12.5px] leading-relaxed"
          >
            {node.text}
          </pre>
        );
      case 'bold':
        return (
          <strong key={k} className="font-semibold">
            {render(node.children, ctx, `${k}-`)}
          </strong>
        );
      case 'italic':
        return <em key={k}>{render(node.children, ctx, `${k}-`)}</em>;
      case 'mention': {
        const name = node.userId ? ctx.nameOf(node.userId) : 'canal';
        const isMe = node.userId !== null && node.userId === ctx.meId;
        const className =
          isMe || node.userId === null
            ? 'rounded bg-highlight/35 px-1 font-medium text-text'
            : 'rounded bg-mention px-1 font-medium text-mention-fg';
        const { userId } = node;
        if (!userId || !name) {
          return (
            <span key={k} className={className}>
              @{name ?? t('usuario')}
            </span>
          );
        }
        return (
          <button
            key={k}
            type="button"
            data-user-card={userId}
            onClick={(event) => openUserCard(userId, event)}
            className={cn(className, 'hover:underline')}
          >
            @{name}
          </button>
        );
      }
      case 'roleMention': {
        const role = ctx.roleOf(node.roleId);
        const isMine = ctx.myRoleIds.includes(node.roleId);
        return (
          <span
            key={k}
            className={cn(
              'rounded px-1 font-medium',
              isMine
                ? 'bg-highlight/35 text-text'
                : role
                  ? roleColors[role.color].chip
                  : 'bg-mention text-mention-fg',
            )}
          >
            @{role?.name ?? t('rol')}
          </span>
        );
      }
      case 'strike':
        return <s key={k}>{render(node.children, ctx, `${k}-`)}</s>;
      case 'taskRef': {
        const label = `${node.key}-${node.number}`;
        const task = ctx.taskOf(node.key, node.number);
        if (!task) return <span key={k}>{label}</span>;
        return (
          <button
            key={k}
            type="button"
            title={task.title}
            onClick={() => ctx.openTask(task.id)}
            className={cn(
              'inline-flex items-baseline gap-1 rounded bg-surface-3/70 px-1 font-medium text-accent hover:underline',
              task.status === 'done' && 'line-through decoration-subtle',
            )}
          >
            <TypeIcon type={task.type} className="self-center" />
            {label}
          </button>
        );
      }
    }
  });
}

/** Renders message text with the safe Markdown subset (never injects HTML). */
export function RichText({ text }: { text: string }) {
  // Works inside and outside a team (e.g. previews): names resolve when team members are known.
  const team = useContext(TeamContext);
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const ctx: MentionContext = {
    taskOf: (key, number) =>
      team && key === team.team.taskKey
        ? queryClient
            .getQueryData<Task[]>(keys.tasks(team.team.id))
            ?.find((t) => t.number === number)
        : undefined,
    openTask: (taskId) => team?.openTask({ mode: 'edit', taskId }),
    nameOf: (id) => team?.memberById(id)?.displayName,
    roleOf: (id) => team?.roleById(id),
    meId: user?.id,
    myRoleIds: (user && team?.memberById(user.id)?.customRoleIds) ?? [],
  };
  return <>{render(parseMarkdown(text), ctx)}</>;
}
